// SEQ-180 phase 2, task P13: how a widget config turns into a number on
// screen, kept as a pure module so the renderer file only exports components.
//
// TWO VALUE PATHS, AND WHY. The eight KPI cards the phase 1 page draws read a
// whole `PnlRollup` (rollup.ts): revenue, cost, profit and every ratio, with
// the ratios recomputed sum-then-divide across the project-grain rows rather
// than averaged from the RPC's per-project figures. Reading those cards'
// values any other way would change the numbers, so a metric with a known
// rollup field keeps using it. Every other metric, including any an
// organisation adds later, is folded straight from the RPC's own bucket
// values by the widget's chosen aggregation.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type { PnlAggregation, PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import {
  PNL_METRIC_KEYS,
  resolveMetricUnit,
  type PnlMetricDefinition,
  type PnlUnit,
} from "../../lib/pnl/pnlAnalytics";
import type { PnlDisplayUnit } from "./pnlAnalyticsFormat";
import type { PnlRollup } from "./rollup";

/** A metric that one of the eight phase 1 KPI cards renders, with the rollup field and display unit it uses. */
interface RollupBinding {
  field: keyof PnlRollup;
  displayUnit: PnlDisplayUnit;
}

export const ROLLUP_BINDING_BY_METRIC: Record<string, RollupBinding> = {
  [PNL_METRIC_KEYS.calculated.totalIncome]: { field: "revenue", displayUnit: "compact-currency" },
  [PNL_METRIC_KEYS.calculated.netProfit]: { field: "profit", displayUnit: "compact-currency" },
  [PNL_METRIC_KEYS.calculated.profitPercentage]: { field: "profitPercent", displayUnit: "percent" },
  [PNL_METRIC_KEYS.calculated.totalCost]: { field: "cost", displayUnit: "compact-currency" },
  [PNL_METRIC_KEYS.labour.overtime]: { field: "overtime", displayUnit: "compact-currency" },
  [PNL_METRIC_KEYS.calculated.overtimePctRevenue]: { field: "overtimePctRevenue", displayUnit: "percent" },
  [PNL_METRIC_KEYS.expense.patching]: { field: "patching", displayUnit: "compact-currency" },
  [PNL_METRIC_KEYS.calculated.patchingPctRevenue]: { field: "patchingPctRevenue", displayUnit: "percent" },
  // expense.materials has no entry on purpose. It used to be bound to "cost",
  // so a Materials KPI showed total cost. PnlRollup has no materials field, so
  // it folds its own fetched values like any other input metric
  // (metricsForWidget always fetches the widget's metric).
  [PNL_METRIC_KEYS.calculated.patchingPctCost]: { field: "patchingPctCost", displayUnit: "percent" },
  [PNL_METRIC_KEYS.labour.ordinaryHours]: { field: "ordinaryHours", displayUnit: "hours" },
  [PNL_METRIC_KEYS.labour.overtimeHours]: { field: "overtimeHours", displayUnit: "hours" },
  [PNL_METRIC_KEYS.calculated.totalLabourHours]: { field: "totalLabourHours", displayUnit: "hours" },
  [PNL_METRIC_KEYS.calculated.overtimeHoursPct]: { field: "overtimeHoursPct", displayUnit: "percent" },
};

/**
 * Why a fold over buckets is refused. Code review H2/H3 (D17): an avg, min,
 * max or latest value the RPC has already folded per bucket cannot be
 * folded again client side without changing its meaning (a mean of
 * per-project means is not the mean; a sum of per-project latest values
 * mixes dates). A widget that wants one such number must fetch one bucket,
 * at `company` grain or its own period grain (dashboardRequestKeys.ts
 * chooses that grain), so this function only ever sees a single row for
 * those methods.
 */
export type FoldRefusal =
  | "avg_across_buckets"
  | "min_across_buckets"
  | "max_across_buckets"
  | "latest_across_buckets"
  | "latest_as_at_differs";

export interface FoldResult {
  value: number | null;
  /** Set when `value` is null because the fold was refused, never for a plain absence of data. */
  refusal: FoldRefusal | null;
}

export const FOLD_REFUSAL_MESSAGES: Record<FoldRefusal, string> = {
  avg_across_buckets: "An average cannot be combined across buckets. Fetch this widget at company grain or its own period grain.",
  min_across_buckets: "A minimum cannot be combined across buckets. Fetch this widget at company grain or its own period grain.",
  max_across_buckets: "A maximum cannot be combined across buckets. Fetch this widget at company grain or its own period grain.",
  latest_across_buckets: "A latest value cannot be combined across buckets. Fetch this widget at company grain or its own period grain.",
  latest_as_at_differs: "The buckets are as at different months, so their latest values cannot be combined.",
};

/**
 * Folds one metric across a set of buckets by the widget's aggregation.
 * Nulls never count as zero: a mean of nothing is not zero, and a `sum` of
 * nothing over buckets that all reported null stays null (rollup.ts's own
 * rule for payroll-source metrics, kept here so the two agree).
 *
 * `sum` is the only aggregation that combines buckets. For avg, min, max
 * and latest the RPC has already executed the fold per bucket (plan section
 * 6.2), so one row passes straight through and more than one is refused
 * with a named reason rather than silently re-folded (H2, H3). `latest` is
 * additionally refused when the rows' `metricsAsAt` for the metric differ,
 * because two snapshots at different dates are not one snapshot.
 */
export function foldMetric(rows: PnlAnalyticsRow[], metricKey: string, aggregation: PnlAggregation): FoldResult {
  const values: number[] = [];
  const asAtMonths = new Set<string>();

  for (const row of rows) {
    const value = row.metrics[metricKey];
    if (value === null || value === undefined) continue;
    values.push(value);
    const asAt = row.metricsAsAt?.[metricKey];
    if (asAt !== undefined) asAtMonths.add(asAt);
  }

  if (values.length === 0) return { value: null, refusal: null };

  switch (aggregation) {
    case "sum":
      return { value: values.reduce((a, b) => a + b, 0), refusal: null };
    case "avg":
      return values.length === 1 ? { value: values[0], refusal: null } : { value: null, refusal: "avg_across_buckets" };
    case "min":
      return values.length === 1 ? { value: values[0], refusal: null } : { value: null, refusal: "min_across_buckets" };
    case "max":
      return values.length === 1 ? { value: values[0], refusal: null } : { value: null, refusal: "max_across_buckets" };
    case "latest":
      if (values.length > 1) return { value: null, refusal: "latest_across_buckets" };
      if (asAtMonths.size > 1) return { value: null, refusal: "latest_as_at_differs" };
      return { value: values[0], refusal: null };
    default:
      return { value: null, refusal: null };
  }
}

export interface ResolvedWidgetValue {
  value: number | null;
  displayUnit: PnlDisplayUnit | null;
  /** Set when the value came from the registry rather than a rollup field, so the caller formats by unit. */
  unit: PnlUnit | null;
  /** H2/H3: set when `value` is null because the fold was refused (see FoldRefusal), so the card can say why. */
  refusal: FoldRefusal | null;
}

/**
 * The single number a KPI widget shows. Prefers the rollup binding (identical
 * to the phase 1 card), falls back to folding the RPC's bucket values.
 */
export function resolveWidgetValue(
  config: PnlWidgetConfig,
  companyTotals: PnlRollup,
  rows: PnlAnalyticsRow[],
  unitByMetric: Record<string, PnlUnit>,
): ResolvedWidgetValue {
  const binding = ROLLUP_BINDING_BY_METRIC[config.metric];
  if (binding) {
    const raw = companyTotals[binding.field];
    return { value: typeof raw === "number" ? raw : null, displayUnit: binding.displayUnit, unit: null, refusal: null };
  }
  const folded = foldMetric(rows, config.metric, config.aggregation);
  return {
    value: folded.value,
    displayUnit: null,
    unit: unitByMetric[config.metric] ?? "currency",
    refusal: folded.refusal,
  };
}

/**
 * Whether a movement is good news, bad news, or neither.
 *
 * Design audit, HIGH: the delta chip used to colour by SIGN alone, so
 * overtime cost rising 40% read green and patching cost falling read red.
 * A number going up is not the same as a number improving.
 *
 * WHY `section` AND NOT `deductible`, which the audit suggested. `deductible`
 * answers "does this reduce net profit in the arithmetic", which is a
 * different question from "is this a cost". Two registry rows prove it, and
 * both were wrong when this keyed on the flag:
 *
 *   labour.overtime      deductible: false, financialInclusion: false
 *   expense.materials    deductible: false (the budget line deducts instead)
 *
 * Overtime is money out of the door whether or not the typed P&L model counts
 * it, and a materials spend is a cost even though the 30% budget line is what
 * gets deducted. Keying on the flag read overtime as neutral (seen live on
 * the preview branch) and would have painted a rising materials cost GREEN.
 *
 * `section` has no such ambiguity: it is required on every registry row and
 * it says which side of the ledger the metric sits on. Revenue up is good,
 * anything in expense, labour or budget up is bad, and the three calculated
 * dollar totals are named explicitly.
 *
 * The ARROW still follows direction, because an arrow describes the movement
 * and the user needs to know which way the number went. Only the COLOUR
 * follows sentiment. A metric the registry has no opinion on is neutral,
 * never guessed at.
 */
export type DeltaSentiment = "good" | "bad" | "neutral";

/**
 * The one delta colour map, shared by the KPI card and the month-on-month
 * table so a contrast fix lands in both. Design audit (contrast): the old
 * #10b981 and #ef4444 measured 2.5:1 and 3.3:1; these are the same hues
 * darkened to emerald 700 (4.7:1) and red 700 (6.4:1). The neutral is
 * --text-secondary (7.5:1), not --text-muted. Keyed on sentiment, never on
 * raw direction, so a rising cost is never green.
 */
export const DELTA_COLOURS: Record<DeltaSentiment, string> = {
  good: "#047857",
  bad: "#b91c1c",
  neutral: "var(--text-secondary)",
};

/** The calculated dollar totals where a rise is unambiguously good news. */
const MORE_IS_BETTER_KEYS: ReadonlySet<string> = new Set([
  PNL_METRIC_KEYS.calculated.netProfit,
  PNL_METRIC_KEYS.calculated.totalIncome,
]);

export function deltaSentiment(
  direction: "up" | "down" | "flat",
  def: PnlMetricDefinition | undefined,
): DeltaSentiment {
  if (direction === "flat" || !def) return "neutral";

  // Only money has an inherent good direction. Fewer hours is good on one
  // project and a stalled job on another, and a ratio's own components
  // already carry the judgement.
  if (resolveMetricUnit(def) !== "currency") return "neutral";

  if (def.section === "revenue") return direction === "up" ? "good" : "bad";
  if (def.section === "expense" || def.section === "labour" || def.section === "budget") {
    return direction === "up" ? "bad" : "good";
  }

  // section === "calculated": a dollar total, named explicitly rather than
  // inferred, because calculated.total_cost and calculated.net_profit sit in
  // the same section and point opposite ways.
  if (MORE_IS_BETTER_KEYS.has(def.metricKey)) return direction === "up" ? "good" : "bad";
  if (def.metricKey === PNL_METRIC_KEYS.calculated.totalCost) return direction === "up" ? "bad" : "good";
  return "neutral";
}

/** The delta between a current and a prior value, or null when either side is unknown. */
export function computeDelta(current: number | null, prior: number | null): { change: number; direction: "up" | "down" | "flat" } | null {
  if (current === null || prior === null) return null;
  const change = current - prior;
  if (change === 0) return { change, direction: "flat" };
  return { change, direction: change > 0 ? "up" : "down" };
}

/** "vs the previous month", used as the caption under a KPI delta chip. */
export const COMPARISON_CAPTIONS: Record<string, string> = {
  previous_month: "vs previous month",
  previous_quarter: "vs previous quarter",
  previous_year: "vs previous year",
  previous_financial_year: "vs previous financial year",
};
